import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { GradientSpinner, RunnerResultCard } from '@/core/components';
import { getNftMediaUrl } from '@/core/services/storage';
import type { Claim } from '@/core/types';
import { downloadFile } from '@/core/utils/download';

interface NftDetailDialogProps {
  claim: Claim | null;
  open: boolean;
  onClose: () => void;
}

export function NftDetailDialog({ claim, open, onClose }: NftDetailDialogProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  useEffect(() => {
    if (!claim || !open) {
      setImageUrl(null);
      setLoading(true);
      return;
    }

    const fetchImage = async () => {
      try {
        if (claim.fileName) {
          const url = await getNftMediaUrl({
            fileName: claim.fileName,
            raceAlias: claim.raceAlias,
            projectId: (claim as { projectId?: string }).projectId,
          });
          setImageUrl(url);
        }
      } catch (err) {
        console.error('Failed to load NFT image:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, [claim, open]);

  const handleDownload = async () => {
    if (!imageUrl) return;
    setDownloading(true);
    try {
      await downloadFile(imageUrl);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!claim) return null;

  const isVideo = imageUrl?.includes('.mp4');
  // heraldingera: raceName, dot99: projectName
  const name = claim.raceName || (claim as { projectName?: string }).projectName || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }} aria-label="閉じる">
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 800,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {name}
        </Typography>

        <Box sx={{ my: 2, textAlign: 'center' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <GradientSpinner />
            </Box>
          ) : imageUrl ? (
            isVideo ? (
              <video src={imageUrl} controls autoPlay muted style={{ maxWidth: '100%', width: 400, borderRadius: 8 }} />
            ) : (
              <img src={imageUrl} alt={name} style={{ maxWidth: '100%', width: 400, borderRadius: 8 }} />
            )
          ) : (
            <Typography color="text.secondary">画像を読み込めませんでした</Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <RunnerResultCard bib={claim.bib} grossTime={claim.time} netTime={claim.netTime} />
        </Box>

        {imageUrl && (
          <Button
            variant="contained"
            fullWidth
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            sx={{
              background: gradient,
              color: '#fff',
              height: 48,
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                background: gradient,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            ダウンロード
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
